<template>
  <div class="progress-page">

    <!-- Standard Viva header — global .viva-page-header handles margin/border -->
    <div class="viva-page-header">
      <div class="viva-header-content">
        <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
        <div v-if="student && selectedCourse" class="header-divider"></div>
        <div v-if="student && selectedCourse" class="header-student-info">
          <div class="header-student-name">{{ student.name }}</div>
          <div class="header-student-course">{{ selectedCourse.title }}</div>
        </div>
      </div>
      <ProfileDropdown
        :student-name="instructorName"
        current-portal="instructor"
        :disable-portal-link="!isVivaAdmin"
      />
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="empty-state" style="margin: 60px 30px;">
      <i class="glyphicon glyphicon-refresh spinning"></i>
      <h3>Loading student progress...</h3>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="empty-state" style="margin: 60px 30px;">
      <i class="glyphicon glyphicon-exclamation-sign"></i>
      <h3>Error</h3>
      <p>{{ error }}</p>
      <button class="viva-btn viva-btn-green viva-btn-small" @click="loadProgress(lastRequestedCourseId)">Try Again</button>
    </div>

    <template v-else>
      <!-- Main content area -->
      <div class="progress-container">
        <div class="progress-tabs">
          <button
            :class="['progress-tab', 'progress-tab--green', { 'progress-tab--active': activeTab === 'vivas' }]"
            @click="switchTab('vivas')"
          >Student's VIVAs</button>
          <button
            v-if="selectedCourse.highlight_reel_available"
            :class="['progress-tab', 'progress-tab--orange', { 'progress-tab--active': activeTab === 'highlights' }]"
            @click="switchTab('highlights')"
          >Highlight Reel</button>
          <!-- VivaScore tab hidden post-launch until there's a plan for showing growth
               over time (UPDATE 10/13) — plot code below is left in place for when it
               comes back, just unreachable from the UI for now.
          <button
            v-if="selectedCourse.available"
            :class="['progress-tab', 'progress-tab--green', { 'progress-tab--active': activeTab === 'vivascore' }]"
            @click="switchTab('vivascore')"
          >VivaScore</button>
          -->
        </div>

        <div :class="['progress-tab-panel', activeTab === 'highlights' ? 'progress-tab-panel--orange' : 'progress-tab-panel--green']">

          <!-- Student VIVAs tab -->
          <div v-if="activeTab === 'vivas'">
            <p v-if="submittedAssignments.length === 0" class="no-vivas-text">
              {{ student.name }} has not completed any VIVAs in this course.
            </p>
            <table v-else class="vivas-table">
              <thead>
                <tr>
                  <th>Watch VIVA</th>
                  <th>Assignment</th>
                  <th>Date Completed</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="asg in submittedAssignments" :key="asg.id">
                  <td>
                    <button
                      class="watch-viva-btn"
                      :disabled="!asg.review_url_param"
                      @click="watchViva(asg)"
                      title="Watch VIVA"
                    ><i class="fa fa-play"></i></button>
                  </td>
                  <td>{{ asg.title }}</td>
                  <td>{{ formatDate(asg.date_submitted) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Highlight Reel tab -->
          <HighlightReelTab
            v-else-if="activeTab === 'highlights'"
            :student-id="student.id"
            :course-id="selectedCourse.id"
          />

          <!-- VivaScore tab hidden post-launch (UPDATE 10/13) — see matching comment on
               the tab button above. Content kept in place, just unreachable.
          <div v-else-if="activeTab === 'vivascore'" class="viva-form-section viva-form-section-green">
            <div class="snapshot-header">
              <div>
                <h3 class="card-heading card-heading--green">Development Snapshot</h3>
                <p class="card-subheading">{{ courseSubtitle }}</p>
              </div>
              <div v-if="submittedAssignments.length > 1" class="view-toggle">
                <div class="view-toggle-buttons">
                  <button
                    :class="['toggle-btn', { 'toggle-btn--active': viewMode === 'trajectory' }]"
                    @click="switchToTrajectory"
                  >{{ submittedAssignments.length }} VIVAs</button>
                  <button
                    :class="['toggle-btn', { 'toggle-btn--active': viewMode === 'single' }]"
                    @click="viewMode = 'single'"
                  >1 VIVA</button>
                </div>
                <select
                  v-if="viewMode === 'single'"
                  v-model="activeAssignmentId"
                  class="assignment-select"
                >
                  <option v-for="asg in submittedAssignments" :key="asg.id" :value="asg.id">
                    {{ asg.title }}
                  </option>
                </select>
              </div>
            </div>

            <div v-if="snapshotMode === 'empty'" class="score-unavailable">
              <p class="unavailable-title">No completed VIVAs yet</p>
              <p class="unavailable-body">
                {{ student.name }} has not completed any VIVAs in this course.
              </p>
            </div>

            <template v-else-if="snapshotMode === 'trajectory'">
              <div class="metric-tabs">
                <button
                  v-for="tab in metricTabs"
                  :key="tab.key"
                  :class="['mtab', { 'mtab--active': activeMetric === tab.key }]"
                  @click="activeMetric = tab.key"
                >{{ tab.label }}</button>
              </div>
              <div class="violin-chart-wrap" :style="violinWrapStyle">
                <VivaViolinChart
                  mode="metrics"
                  :metrics="trajectoryData"
                  :student-trend="true"
                />
              </div>
              <div class="chart-legend">
                <div class="legend-item">
                  <div class="legend-dot legend-dot--orange"></div>
                  <span>{{ student.name }}</span>
                </div>
                <div class="legend-item">
                  <div class="legend-dot legend-dot--grey"></div>
                  <span>Classmates</span>
                </div>
              </div>
            </template>

            <template v-else-if="snapshotMode === 'single'">
              <div class="violin-chart-wrap" :style="violinWrapStyle">
                <VivaViolinChart
                  mode="metrics"
                  :metrics="singleVivaData"
                />
              </div>
              <div class="chart-legend">
                <div class="legend-item">
                  <div class="legend-dot legend-dot--orange"></div>
                  <span>{{ student.name }}</span>
                </div>
                <div class="legend-item">
                  <div class="legend-dot legend-dot--grey"></div>
                  <span>Classmates</span>
                </div>
              </div>
            </template>
          </div>
          -->

        </div>

        <!-- Back Link -->
        <div class="back-section">
          <a href="#" @click.prevent="goBack" class="back-link">
            <span>←</span> Back
          </a>
        </div>
      </div>
    </template>

  </div>
</template>

<script>
import apiClient from '../utils/apiClient.js';
import env from '../utils/env.js';
import { handleApiError } from '../utils/errorHandler.js';
import { getStatusData } from '../utils/statusCache.js';
import VivaViolinChart from '@/components/VivaViolinChart.vue';
import ProfileDropdown from '@/components/ProfileDropdown.vue';
import HighlightReelTab from '@/components/HighlightReelTab.vue';

// Vocabulary Range and Academic Language are intentionally not listed here — see
// utils/vivascore.js's METRICS for why.
const METRIC_TABS = [
  { key: 'VS', label: 'VivaScore', field: 'vivascore' },
  { key: 'RF', label: 'Fluency',   field: 'response_fluency' },
  { key: 'AX', label: 'Alignment', field: 'semantic_sim' },
];

export default {
  name: 'StudentProgress',

  components: { VivaViolinChart, ProfileDropdown, HighlightReelTab },

  data() {
    return {
      loading: true,
      error: null,
      student: null,
      instructorName: '',
      isVivaAdmin: false,
      selectedCourse: null,
      activeAssignmentId: null,
      activeTab: 'vivas',
      viewMode: 'trajectory',
      activeMetric: 'VS',
      metricTabs: METRIC_TABS,
      loadSeq: 0,
      lastRequestedCourseId: null,
    };
  },

  computed: {
    submittedAssignments() {
      if (!this.selectedCourse) return [];
      return this.selectedCourse.assignments.filter(a => a.is_submitted);
    },

    snapshotMode() {
      const n = this.submittedAssignments.length;
      if (n === 0) return 'empty';
      if (n === 1) return 'single';  // no trajectory possible with 1
      return this.viewMode;
    },

    courseSubtitle() {
      if (!this.selectedCourse) return '';
      return [this.selectedCourse.title, this.selectedCourse.term, this.selectedCourse.year]
        .filter(Boolean).join(' · ');
    },

    // Multi-VIVA trajectory: one violin per submitted assignment, rendered through the
    // chart's anonymous Mode A contract + studentTrend (the payload carries no peer
    // student ids — students will eventually see this page). The metric tabs switch
    // which z/peers_z pair feeds the violins; all values come from asg.vivascore, the
    // same serialize_vivascore payload every VivaScore display uses.
    trajectoryData() {
      const field = METRIC_TABS.find(t => t.key === this.activeMetric)?.field || 'vivascore';
      return this.submittedAssignments.map(asg => ({
        // VivaViolinChart's x-axis labels aren't truncated by the chart itself —
        // callers are expected to shorten long titles before passing them in
        // (see StudentSubmissions.vue's identical 20-char truncation), or
        // adjacent long titles overlap.
        label: asg.title.length > 20 ? asg.title.slice(0, 19) + '…' : asg.title,
        studentZ: field === 'vivascore'
          ? asg.vivascore.vivascore_z
          : asg.vivascore.metrics[field].z,
        peers: field === 'vivascore'
          ? asg.vivascore.vivascore_peers_z
          : asg.vivascore.metrics[field].peers_z,
      }));
    },

    // Single-VIVA view: VivaScore + 2 metric violins for the selected assignment.
    // Peers are that assignment's anonymous class z's, including this student's own
    // (the orange studentZ dot sits over one grey peer dot).
    singleVivaData() {
      if (this.submittedAssignments.length === 0) return [];
      const asg = this.submittedAssignments.find(a => a.id === this.activeAssignmentId)
        || this.submittedAssignments[this.submittedAssignments.length - 1];
      const vs = asg.vivascore;
      return [
        { label: 'VivaScore', studentZ: vs.vivascore_z, peers: vs.vivascore_peers_z },
        { label: 'Fluency', studentZ: vs.metrics.response_fluency.z, peers: vs.metrics.response_fluency.peers_z },
        { label: 'Alignment', studentZ: vs.metrics.semantic_sim.z, peers: vs.metrics.semantic_sim.peers_z },
      ];
    },

    // Dynamic max-width for the violin wrapper so rendered height stays ~400px
    // regardless of how many violins are shown. SVG keeps width:100% and
    // auto-scales from the wrapper width — no ::v-deep height override needed.
    violinWrapStyle() {
      const targetHeight = 400;
      const leftOffset = 44;   // matches VivaViolinChart's leftOffset
      const colW = 112;        // matches VivaViolinChart's colW
      const viewBoxH = 220;    // matches VivaViolinChart's viewBox height
      const n = this.snapshotMode === 'single'
        ? 3   // VivaScore + Response Fluency + Content Alignment (singleVivaData's length)
        : Math.max(this.submittedAssignments.length, 1);
      const maxPx = Math.round(targetHeight * (leftOffset + n * colW) / viewBoxH);
      return { maxWidth: maxPx + 'px' };
    },
  },

  mounted() {
    const statusData = getStatusData();
    if (statusData) {
      this.isVivaAdmin = statusData.is_viva_admin || false;
    }
    const courseId = this.$route.query.course_id;
    // 'vivascore' intentionally excluded while the tab is hidden (UPDATE 10/13) — a
    // stale deep link should fall back to the default tab, not render nothing.
    if (['vivas', 'highlights'].includes(this.$route.query.tab)) {
      this.activeTab = this.$route.query.tab;
    }
    this.loadProgress(courseId);
  },

  methods: {
    switchToTrajectory() {
      this.viewMode = 'trajectory';
      const last = this.submittedAssignments[this.submittedAssignments.length - 1];
      this.activeAssignmentId = last ? last.id : null;
    },

    async loadProgress(courseId) {
      this.loading = true;
      this.error = null;
      this.viewMode = 'trajectory';
      this.lastRequestedCourseId = courseId || null;
      const requestId = ++this.loadSeq;
      const studentId = this.$route.params.student_id;
      const params = courseId ? { course_id: courseId } : {};

      try {
        const response = await apiClient.get(
          `/api/viva/instructor/student/${studentId}/progress/`,
          { params }
        );
        if (requestId !== this.loadSeq) return;
        const data = response.data;
        this.student = data.student;
        this.instructorName = data.instructor_name;
        this.selectedCourse = data.selected_course;
        const submitted = data.selected_course.assignments.filter(a => a.is_submitted);
        this.activeAssignmentId = submitted.length > 0 ? submitted[submitted.length - 1].id : null;
        this.activeMetric = 'VS';
        // The VivaScore tab only exists when available - if a deep link asked for it
        // on a course where it isn't, fall back to the default tab rather than
        // rendering nothing.
        if (this.activeTab === 'vivascore' && !this.selectedCourse.available) {
          this.activeTab = 'vivas';
        }
        // Same fallback for the Highlight Reel tab - FeatureFlag_HighlightReel off
        // means the tab button itself is hidden, so a stale ?tab=highlights deep
        // link must not leave the page on a tab with no button to reach it.
        if (this.activeTab === 'highlights' && !this.selectedCourse.highlight_reel_available) {
          this.activeTab = 'vivas';
        }
      } catch (err) {
        if (requestId !== this.loadSeq) return;
        if (courseId && err.response && err.response.status === 404) {
          // Stale course link — the course was deleted or the student removed from it.
          // The backend 404s rather than silently serving a different course, so fall
          // back to the default course view here. (No courseId on the retry, so a
          // student who is in none of this instructor's courses still errors below.)
          console.warn(`Course ${courseId} not available for this student; falling back to default course`);
          return this.loadProgress();
        }
        this.error = handleApiError(err, this.$router, 'Failed to load student progress.');
      } finally {
        if (requestId === this.loadSeq) this.loading = false;
      }
    },

    switchTab(tab) {
      this.activeTab = tab;
      this.$router.replace({ query: { ...this.$route.query, tab } }).catch(() => {});
    },

    watchViva(assignment) {
      if (!assignment.review_url_param) return;
      window.open(`${env.serverAddr}/viva/review-student/?v=${assignment.review_url_param}`, '_blank');
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
      });
    },

    goBack() {
      if (this.selectedCourse) {
        this.$router.push({ name: 'course-assignments', params: { id: this.selectedCourse.id } });
      } else {
        this.$router.push({ name: 'instructor-dashboard' });
      }
    },
  },
};
</script>

<style scoped>
.progress-page {
  min-height: 100vh;
  background-color: var(--viva-cream);
  padding-bottom: 100px;
  /* Scales this whole page (including HighlightReelTab, rendered inside it)
     up 25% per request — not just font-size, so proportions stay intact. */
  zoom: 1.25;
}

/* Main content container */
.progress-container {
  margin: 20px 30px;
}

/* Tabs: Student VIVAs / Highlight Reel / VivaScore — angled ribbon shape,
   each tab overlapping the next, like a stack of folder tabs. */
.progress-tabs {
  display: flex;
}

.progress-tab {
  position: relative;
  padding: 14px 36px 14px 52px;
  min-width: 180px;
  border: none;
  background: #E8E4DC;
  color: var(--viva-grey);
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  font-family: 'poppins', sans-serif;
  transition: background 0.15s ease, color 0.15s ease;
  /* Folder-tab silhouette: a slanted parallelogram with each sharp corner
     chamfered by ~8px so it reads as rounded rather than spiky (a true
     border-radius arc is clipped away by the polygon, so this is the closest
     clip-path-only approximation — see the mockup's rounded tab shape). */
  clip-path: polygon(
    20px 8px, 32px 0,
    calc(100% - 8px) 0, calc(100% - 4px) 8px,
    calc(100% - 20px) calc(100% - 8px), calc(100% - 32px) 100%,
    8px 100%, 4px calc(100% - 8px)
  );
  margin-right: -22px;
  z-index: 1;
}

.progress-tab:first-child {
  /* Left edge is a true (unclipped) box edge, so border-radius actually
     rounds it — only the right edge needs the chamfered-polygon treatment. */
  clip-path: polygon(
    0 0,
    calc(100% - 8px) 0, calc(100% - 4px) 8px,
    calc(100% - 20px) calc(100% - 8px), calc(100% - 32px) 100%,
    0 100%
  );
  border-radius: 20px 0 0 20px;
  padding-left: 28px;
}

.progress-tab--active {
  z-index: 3;
}

.progress-tab--green {
  background: #DCEEE2;
  color: var(--viva-green);
}

.progress-tab--orange {
  background: #FBE7D2;
  color: var(--viva-orange);
}

.progress-tab--active.progress-tab--green {
  background: var(--viva-green);
  color: white;
}

.progress-tab--active.progress-tab--orange {
  background: var(--viva-orange);
  color: white;
}

.progress-tab-panel {
  background: white;
  border-radius: 0 10px 10px 10px;
  border: 2px solid;
  padding: 24px;
}

.progress-tab-panel--green {
  border-color: var(--viva-green);
}

.progress-tab-panel--orange {
  border-color: var(--viva-orange);
}

/* Student VIVAs tab */
.vivas-table {
  width: 100%;
  border-collapse: collapse;
}

.vivas-table th {
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--viva-grey);
  padding: 10px 14px;
  border-bottom: 2px solid #EDE9E0;
}

.vivas-table td {
  padding: 12px 14px;
  border-bottom: 1px solid #F0EBE0;
  color: var(--viva-grey);
  font-size: 16px;
  font-weight: 400;
}

.watch-viva-btn {
  background: var(--viva-orange);
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  cursor: pointer;
}

.watch-viva-btn:disabled {
  background: #D8D4CC;
  cursor: not-allowed;
}

.back-section {
  margin-top: 24px;
}

/* .back-link itself is intentionally left to the global viva-classes.css rule
   (orange, green on hover/focus) — the same convention every other back link
   in the VIVA app uses. */

/* Card headings — override global p/h3 for the data cards */
.card-heading {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin: 0 0 6px 0;
  font-family: 'poppins', sans-serif;
}

.card-heading--green {
  color: var(--viva-green);
}

.card-heading--orange {
  color: var(--viva-orange);
  margin-bottom: 20px;
}

.card-subheading {
  font-size: 14px;
  color: var(--viva-grey);
  margin: 0 0 16px 0;
}

/* Snapshot header row */
.snapshot-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 4px;
}

/* Score unavailable / empty state within card */
.score-unavailable {
  background: #F7F4EE;
  border-left: 3px solid #D8D4CC;
  border-radius: 0 8px 8px 0;
  padding: 14px 18px;
  margin-top: 4px;
}

.unavailable-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--viva-dark);
  margin: 0 0 6px 0;
}

.unavailable-body {
  font-size: 14px;
  color: var(--viva-grey);
  margin: 0;
  line-height: 1.6;
}

/* View toggle (N VIVAs / 1 VIVA), plus the assignment picker that appears
   underneath it once "1 VIVA" is selected. */
.view-toggle {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
  margin-left: 12px;
}

.view-toggle-buttons {
  display: flex;
  gap: 4px;
}

.assignment-select {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  cursor: pointer;
  border: 1.5px solid #D8D4CC;
  color: var(--viva-grey);
  background: var(--viva-white);
  font-family: 'poppins', sans-serif;
  max-width: 220px;
}

.toggle-btn {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  cursor: pointer;
  border: 1.5px solid #D8D4CC;
  color: var(--viva-grey);
  background: transparent;
  font-family: 'poppins', sans-serif;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.toggle-btn--active {
  background: var(--viva-dark);
  color: white;
  border-color: var(--viva-dark);
  font-weight: 500;
}

/* Metric tabs */
.metric-tabs {
  display: flex;
  gap: 6px;
  flex-wrap: nowrap;
  margin-bottom: 16px;
  overflow-x: auto;
}

.mtab {
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  cursor: pointer;
  border: 1.5px solid #D8D4CC;
  color: var(--viva-grey);
  background: transparent;
  font-family: 'poppins', sans-serif;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.mtab--active {
  background: var(--viva-orange);
  color: white;
  border-color: var(--viva-orange);
  font-weight: 500;
}

.violin-chart-wrap {
  display: block;
}

/* Chart legend */
.chart-legend {
  display: flex;
  gap: 20px;
  justify-content: center;
  align-items: center;
  margin-top: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--viva-grey);
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-dot--orange { background: var(--viva-orange); }
.legend-dot--grey   { background: #C4C0B8; }

.no-vivas-text {
  font-size: 13px;
  color: #C4C0B4;
  font-style: italic;
  margin: 0;
}
</style>
